<template>
  <div class="content-data-tutor" :class="{ leaving: isLeaving }">
    <form class="content-form-tutor" autocomplete="off" id="validaBeneficiario">
      <div class="container-content">
        <div class="group-content-curp">
          <div class="content-curp">
            <input v-model="curp" placeholder="CURP del menor" autocomplete="off" @keyup="validateCURP" v-maska
              :data-maska='regexMask' class="inputSuccess" data-maska-tokens="H:[hmHM]"
              style="text-transform: uppercase;" minlength="18" maxlength="18"
              :class='{ inputSuccess: curpFlag, inputDanger: curpFlag === false }' required />
            <small :class='{ successText: curpFlag, errorText: curpFlag === false }'>
              {{ textCurp }}
            </small>
          </div>
        </div>
        <div class="group-content-email">
          <div class="content-email">
            <input v-model="email" placeholder="correo@ejemplo.com" @keyup="validateEmail()" autocomplete="off"
              class="inputSuccess" :class='{ inputSuccess: emailFlag, inputDanger: emailFlag === false }' required />
            <small :class='{ successText: emailFlag, errorText: emailFlag === false }'>
              {{ textValidationEmail }}
            </small>
          </div>
        </div>
      </div>

      <div class="container-avise-privacity">
        <h3 class="text-avise">
          <span @keyup="showContentDocument(true, 'TERMINOS_Y_CONDICIONES')"
            @click="showContentDocument(true, 'TERMINOS_Y_CONDICIONES')">
            Consulta nuestro aviso de privacidad
          </span>
        </h3>
      </div>

      <!-- ✅ reCAPTCHA solo visible cuando CURP y correo son válidos -->
      <div class="container-recaptcha" v-if="inputsValidos">
        <div v-if="recaptchaLoading" class="recaptcha-loader">
          <div class="recaptcha-spinner"></div>
          <p class="recaptcha-loading-text">Cargando verificación...</p>
        </div>
        <div ref="recaptchaContainer" :style="{ visibility: recaptchaLoading ? 'hidden' : 'visible' }"></div>
      </div>

      <!-- ✅ Mensaje guía cuando los inputs aún no están completos -->
      <div class="container-recaptcha-hint" v-else>
        <p class="recaptcha-hint-text">
          Completa el CURP y correo para activar la verificación
        </p>
      </div>

      <AlertView :modelAvise="modelAvise" />

      <div class="container-btns">
        <div class="container-size-btn">
          <button class="btn-gray success-btn" type="button" @click='goToLanding()'>
            <span class="btn-text-gray">
              <img src="@/assets/icons/arrowBefore.png" alt="bienestar-regresa" class="arrow-left" />
              Regresar
            </span>
          </button>
        </div>
        <div class="container-size-btn">
          <button class="btn-gray" type="button" @click="continuity" :class="{
            'success-btn': (curpFlag && emailFlag && recaptchaVerified),
            'error-btn': (!curpFlag || !emailFlag || !recaptchaVerified)
          }">
            <span class="btn-text-gray">
              Siguiente
              <img src="@/assets/icons/arrowRight.png" alt="bienestar-siguiente" class="arrow-right" />
            </span>
          </button>
        </div>
      </div>
    </form>
  </div>
  <DocumentModalView :propertieModals="propertieModals" v-if="propertieModals.status" />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import DocumentModalView from '@/core/utils/documents/DocumentModalView.vue';
import AlertView from '@/core/components/AlertView.vue';
import * as useRegex from '@/core/utils/regex';
import environmentConfig from '@/environment/environment';

const LEAVE_TIMEOUT_MS = 500

const modelAvise = ref({
  type: 'info',
  text: 'El estado de cuenta y los movimientos se enviarán al correo electrónico que ingresaste.',
});

const recaptchaSiteKey = environmentConfig.environment.recaptchaSiteKey;
const recaptchaVerified = ref(false);
const recaptchaToken = ref('');
const recaptchaContainer = ref(null);
const recaptchaWidgetId = ref(null);
const regexMask = ref('@@@@######H@@@@@**');
const textValidationEmail = ref('Ingresa el correo del tutor registrado en la App Bienestar Azteca');
const textCurp = ref('Ingresa los 18 caracteres');
const curpFlag = ref(null);
const propertieModals = ref({ status: false, type: '' });
const emailFlag = ref(null);
const useRegexEmail = useRegex.default.emailRegex;
const useRegexCURP = useRegex.default.curpRegex;
const curp = ref('');
const email = ref('');
const controlTutor = ref(0);
const recaptchaLoading = ref(true);
const isLeaving = ref(false);
const recaptchaRendered = ref(false);

const emits = defineEmits(['eventEmit', 'eventEmitBack'])

// ✅ Computed — ambos inputs válidos
const inputsValidos = computed(() => curpFlag.value === true && emailFlag.value === true)

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script'

// ✅ Cuando los inputs se vuelven válidos, renderizar el reCAPTCHA
watch(inputsValidos, valido => {
  if (valido && !recaptchaRendered.value) {
    recaptchaLoading.value = true
    nextTick(() => {
      if (window.grecaptcha && window.grecaptcha.render) {
        renderRecaptcha()
      } else {
        loadRecaptchaScript()
      }
    })
  } else if (!valido) {
    // Si los inputs dejan de ser válidos, resetear el estado del captcha
    recaptchaVerified.value = false
    recaptchaToken.value = ''
    recaptchaRendered.value = false
    recaptchaLoading.value = true
  } else {
    // valido=true pero ya renderizado — no hacer nada
  }
})

const goToLanding = () => {
  isLeaving.value = true
  setTimeout(() => {
    emits('eventEmitBack', true)
  }, LEAVE_TIMEOUT_MS)
}

function resetRecaptcha() {
  recaptchaVerified.value = false
  recaptchaToken.value = ''
  // ✅ Si el widget ya está renderizado, grecaptcha.reset() solo limpia el
  // check (no lo destruye) — NO tocar recaptchaRendered/recaptchaLoading
  // aquí, o el watcher de inputsValidos nunca vuelve a dispararse (porque
  // el valor no cambia) y el loader "Cargando verificación..." se queda
  // pegado para siempre cuando el backend rechaza los datos.
  try {
    if (recaptchaWidgetId.value !== null && window.grecaptcha) {
      window.grecaptcha.reset(recaptchaWidgetId.value)
    } else {
      // No había widget renderizado todavía: sí hay que forzar el estado
      // de carga para que el watcher lo vuelva a intentar más adelante.
      recaptchaRendered.value = false
      recaptchaLoading.value = true
    }
  } catch (e) {
    console.warn('No se pudo resetear el reCAPTCHA:', e)
    recaptchaRendered.value = false
    recaptchaLoading.value = true
  }
}

function loadRecaptchaScript() {
  if (document.getElementById(RECAPTCHA_SCRIPT_ID)) {
    if (window.grecaptcha && window.grecaptcha.render) {
      renderRecaptcha()
    } else {
      window.grecaptcha?.ready?.(() => renderRecaptcha())
    }
    return
  }
  window.onRecaptchaReady = () => renderRecaptcha()
  const script = document.createElement('script')
  script.id = RECAPTCHA_SCRIPT_ID
  script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaReady&render=explicit'
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

function showContentDocument(show = false, typeDocument = 'TERMINOS_Y_CONDICIONES') {
  const objNavigator = navigator?.userAgent?.toLowerCase();
  if (objNavigator.includes('safari') && objNavigator.includes('iphone')) {
    window.location = 'https://d1mq21xo4bjiqa.cloudfront.net/documentos/aviso_privacidad_integral_mayores.pdf';
  } else {
    propertieModals.value = { status: show, type: typeDocument };
  }
}

async function continuity() {
  // ✅ Validar orden: primero inputs, luego captcha
  if (!curpFlag.value || !emailFlag.value) {
    return; // El botón ya está deshabilitado visualmente, no hacer nada
  }

  if (!recaptchaVerified.value) {
    return; // El botón ya está deshabilitado visualmente, no hacer nada
  }

  controlTutor.value += 1;

  if (controlTutor.value === 1) {
    const modelData = {
      curp: curp.value.toUpperCase(),
      correo: email.value,
      token: recaptchaToken.value,
    };
    const emitValues = {
      continueFlow: true,
      data: modelData,
      componentName: 'ebuuvups',
    };
    emits('eventEmit', emitValues);
    controlTutor.value = 0;
    resetRecaptcha()
  }
}

defineExpose({ resetRecaptcha })

function validateCURP(ev) {
  if (ev.target.value !== null && ev.target.value !== '' && ev.target.value.length === 18) {
    const tempFlager = curp.value.match(useRegexCURP);
    if (tempFlager) {
      curpFlag.value = true;
      textCurp.value = 'Ingresa los 18 caracteres';
    } else {
      textCurp.value = 'CURP incorrecto';
      curpFlag.value = false;
    }
  } else {
    textCurp.value = 'CURP incorrecto';
    curpFlag.value = false;
  }
}

function validateEmail() {
  email.value = email.value.trim();
  if (email.value.match(useRegexEmail)) {
    emailFlag.value = true;
    textValidationEmail.value = 'Ingresa el correo del tutor registrado en app bienestar azteca';
  } else {
    emailFlag.value = false;
    textValidationEmail.value = 'Correo incorrecto';
  }
}

function renderRecaptcha() {
  if (!window.grecaptcha || !recaptchaContainer.value) return
  try {
    recaptchaContainer.value.innerHTML = ''
    recaptchaWidgetId.value = window.grecaptcha.render(recaptchaContainer.value, {
      sitekey: recaptchaSiteKey,
      callback: (token) => {
        recaptchaVerified.value = true
        recaptchaToken.value = token
      },
      'expired-callback': () => {
        recaptchaVerified.value = false
        recaptchaToken.value = ''
      },
      'error-callback': () => {
        recaptchaVerified.value = false
        recaptchaToken.value = ''
      }
    })
    recaptchaRendered.value = true
    recaptchaLoading.value = false
  } catch (e) {
    console.warn('Error al renderizar reCAPTCHA:', e)
    recaptchaLoading.value = false
  }
}

onMounted(() => {
  // ✅ Solo cargar el script pero NO renderizar hasta que los inputs sean válidos
  if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = RECAPTCHA_SCRIPT_ID
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }
})

onUnmounted(() => {
  delete window.onRecaptchaReady
})
</script>

<style scoped>
.content-data-tutor {
  width: 80%;
  margin: auto;
  padding-top: 60px;
  animation: fadeSlideIn 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards;
}

.content-form-tutor {
  width: 90%;
  margin: auto;
}

.container-content {
  display: flex;
  justify-content: center;
  margin: auto;
}

.group-content-curp {
  width: 50%;
}

.content-curp {
  width: 70%;
  margin: auto;
  text-align: left;
}

.group-content-email {
  width: 50%;
}

.content-email {
  width: 70%;
  margin: auto;
  text-align: left;
}

.container-avise-privacity {
  margin-top: 50px;
  margin-bottom: 40px;
}

.container-avise-privacity .text-avise {
  cursor: pointer;
  color: #5f6368;
  font-size: 20px;
}

.container-avise-privacity .text-avise span {
  color: #5f6368;
  cursor: pointer;
  text-decoration: underline;
}

.container-recaptcha {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px auto;
}

/* ✅ Hint cuando aún no hay inputs válidos */
.container-recaptcha-hint {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px auto;
  height: 78px;
}

.recaptcha-hint-text {
  font-size: 13px;
  color: #aaa;
  margin: 0;
  text-align: center;
}

.container-btns {
  display: flex;
  justify-content: center;
  margin: auto;
  margin-top: 50px;
  margin-bottom: 20px;
}

.container-size-btn {
  width: 50%;
}

.btn-gray {
  background-color: #979797;
  width: 55%;
  height: 50px;
  border-radius: 10px;
}

.btn-gray .btn-text-gray {
  display: flex;
  font-size: 16px !important;
  justify-content: center;
  align-items: center;
}

input {
  height: 40px;
  outline: none;
  padding-left: 20px;
  width: 100%;
  border-radius: 45px;
  box-shadow: 2px 2px 19px 0 rgba(0, 0, 0, 0.2);
  background-color: #fff;
}

.inputSuccess {
  border: solid 1px rgba(32, 33, 36, 0.2);
}

.inputDanger {
  border: solid 1px #f4354b;
}

small {
  font-size: 12px;
}

button {
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  border-radius: 10px;
  outline: none;
  border: none;
  padding: 0px 10px 0px 10px;
}

.success-btn {
  background-color: #0c825e;
  cursor: pointer;
}

.error-btn {
  background-color: #979797;
  cursor: auto;
}

.arrow-right {
  width: 20px;
  margin-left: 10px;
}

.arrow-left {
  width: 20px;
  margin-right: 10px;
}

.successText {
  color: rgba(32, 33, 36, 0.5);
}

.errorText {
  color: #f4354b !important;
}

.recaptcha-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 78px;
  gap: 10px;
}

.recaptcha-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e0e0e0;
  border-top: 3px solid #0c825e;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-data-tutor.leaving {
  animation: fadeSlideOut 0.45s cubic-bezier(0.55, 0, 1, 0.45) forwards;
}

@keyframes fadeSlideOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(30px);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.recaptcha-loading-text {
  font-size: 13px;
  color: #999;
  margin: 0;
}

@media (max-width: 900px) {
  .content-data-tutor {
    padding-top: 40px;
  }
}

@media only screen and (min-width: 768px) and (max-width: 1024px) {
  .content-data-tutor {
    width: 90%;
    padding-top: 40px;
  }
  .content-form-tutor {
    width: 95%;
  }
  .container-content {
    display: flex;
    gap: 20px;
  }
  .group-content-curp {
    width: 50%;
  }
  .content-curp {
    width: 90%;
  }
  .group-content-email {
    width: 50%;
  }
  .content-email {
    width: 90%;
  }
  input {
    height: 48px;
    font-size: 16px;
  }
  small {
    font-size: 14px;
  }
  .container-avise-privacity .text-avise {
    font-size: 18px;
  }
  .container-recaptcha {
    transform: scale(1);
    margin: 30px auto;
  }
  .btn-gray {
    width: 75%;
    height: 52px;
  }
  .btn-text-gray {
    font-size: 15px !important;
  }
  .container-btns {
    margin-top: 40px;
  }
}

@media only screen and (min-width: 200px) and (max-width: 530px) {
  .content-data-tutor {
    margin: auto;
    padding-top: 30px;
  }
  .content-form-tutor {
    width: 100%;
    margin: auto;
  }
  .container-content {
    display: flow-root;
    justify-content: center;
  }
  .group-content-curp {
    width: 100%;
  }
  .content-curp {
    width: 100%;
    margin: auto;
    float: left;
    margin-bottom: 30px;
  }
  .group-content-email {
    width: 100%;
  }
  .content-email {
    width: 100%;
    margin: auto;
    text-align: left;
  }
  input {
    width: calc(100% - 20px);
  }
  .container-avise-privacity {
    margin-top: 40px;
    margin-bottom: 20px;
  }
  .container-avise-privacity .text-avise span {
    font-size: 14px;
  }
  .container-recaptcha {
    transform: scale(0.85);
    transform-origin: center;
    margin: 20px auto;
  }
  .container-btns {
    margin-top: 40px;
    margin-bottom: 20px;
  }
  .container-size-btn {
    width: 50%;
  }
  .btn-gray {
    width: 90%;
    height: 50px;
  }
  .btn-text-gray {
    font-size: 12px !important;
  }
}

@media only screen and (min-width: 360px) and (max-width: 413px) {
  .btn-text-gray {
    font-size: 12px !important;
  }
}

@media only screen and (min-width: 567px) and (max-width: 1025px) and (min-height: 300px) and (max-height: 730px) and (orientation: landscape) {
  .btn-gray .btn-text-gray {
    font-size: 12px !important;
  }
}
</style>